import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, input } from '@angular/core';
import { combineLatestWith, fromEvent, switchMap, take } from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { filter, map, tap } from 'rxjs/operators';
import { isDefined } from '@core';
import { BackButtonComponent } from '@app/components/back-button/back-button.component';

export interface PageHeaderBackSwipeConfig {
  element: HTMLElement;
  threshold?: number; // Percentage of the element width to trigger the back action
  duration?: number; // Duration of the swipe effect
}

@Component({
  selector: 'app-page-header',
  standalone: true,
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
  host: {
    'role': 'button',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BackButtonComponent,
  ],
})
export class PageHeaderComponent implements AfterViewInit {

  public readonly title = input<string | undefined>();

  public readonly backButton = input<boolean>(true);
  public readonly backSwipe = input<PageHeaderBackSwipeConfig>();

  private readonly backSwipe$ = toObservable(this.backSwipe);

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly destroyRef: DestroyRef,
  ) {}

  public ngAfterViewInit() {
    const backButton = this.elementRef.nativeElement.getElementsByClassName('back')[0];

    this.backSwipe$.pipe(
      filter(isDefined),
      switchMap((config) => {
        return fromEvent<TouchEvent>(config.element, 'touchstart').pipe(
          map((touchstart) => (
            {
              config,
              touchstart,
            }
          )),
        );
      }),
      filter(({ config, touchstart }) => {
        const threshold = Math.max(Math.min(1, config.threshold ?? 0.1), 0.01);
        return touchstart.touches[0].pageX - config.element.clientLeft < threshold * config.element.clientWidth;
      }),
      switchMap(({ config, touchstart }) => fromEvent<TouchEvent>(config.element, 'touchmove').pipe(
        filter((touchmove) => touchstart.timeStamp <= touchmove.timeStamp),
        map((touchmove) => touchmove.touches[0].clientX - touchstart.touches[0].clientX),
        filter((distance) => distance > 0),
        tap((distance) => {
          const scale = Math.max(1,
            1 + (
              distance / config.element.clientWidth
            ) * 2,
          );
          backButton.setAttribute('style', `transform: scale(${ scale })`);
        }),
        combineLatestWith(fromEvent<MouseEvent>(config.element, 'touchend')),
        take(1),
        map(([distance, touchend]) => (
          {
            config,
            touchstart,
            distance,
            touchend,
          }
        )),
      )),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(({ config, distance }) => {
      const duration = Math.max(Math.min(1, config.duration ?? 0.5), 0.1);

      if (distance > duration * config.element.clientWidth) {
        this.elementRef.nativeElement.click();
      }

      backButton.animate([{ transform: 'scale(1)' }], {
        duration: 300,
        easing: 'ease-in-out',
      }).onfinish = () => {
        backButton.setAttribute('style', `transform: scale(1)`);
      };
    });
  }

}
